import { logger } from '@/lib/logger';
import { resolveHomeSurvey } from '@/lib/userHomeSurvey';
import { supabase } from '@/lib/supabase';
import type { HomeSurveyConfigPayload } from '@/types';

export interface SetHomeResult {
  ok: boolean;
  /** Supabase Edge updated user_preferences. */
  supabase: boolean;
  /** Preferences metadata returned by the edge function (for immediate UI). */
  metadata?: Record<string, unknown>;
  /** Config used unknown instrument/level slugs. */
  invalid?: boolean;
}

type EdgeSetHomeResponse = {
  success?: boolean;
  metadata?: Record<string, unknown>;
  updated?: { level?: string; instrument?: string };
};

/**
 * Saves instrument + level through the `set-user-home-survey` edge function,
 * which writes Supabase and then pushes the same answers to WordPress
 * server-to-server. The browser must not make the WordPress write itself: the
 * app session outlives the WP login cookie, and a cookie-less admin-ajax call
 * fails. A WordPress failure is recorded server-side and never fails the save.
 */
export async function setHomeDashboard(cfg: HomeSurveyConfigPayload): Promise<SetHomeResult> {
  const payloads = resolveHomeSurvey(cfg);
  if (!payloads) {
    logger.error('[set-home] unknown home_survey_config slugs', { cfg });
    return { ok: false, supabase: false, invalid: true };
  }

  let sb = false;
  let metadata: Record<string, unknown> | undefined;
  try {
    const { data, error } = await supabase.functions.invoke<EdgeSetHomeResponse>('set-user-home-survey', {
      method: 'POST',
      body: {
        level: payloads.level,
        instrument: payloads.instrument,
      },
    });
    if (error) {
      logger.error('[set-home] edge function error', { error: error.message, name: error.name });
    } else {
      sb = data?.success === true;
      if (data?.metadata && typeof data.metadata === 'object') {
        metadata = data.metadata as Record<string, unknown>;
      } else if (sb && data?.updated) {
        metadata = { level: data.updated.level, instrument: data.updated.instrument };
      }
      if (!sb) logger.warn('[set-home] edge function unexpected body', { data });
    }
  } catch (err) {
    logger.error('[set-home] edge function threw', { err });
  }

  return { ok: sb, supabase: sb, metadata };
}

export function setHomeFailureMessage(result: SetHomeResult): string {
  if (result.invalid) {
    return 'This dashboard is misconfigured. Contact support.';
  }
  return "Couldn't set as home. Try again.";
}
